import { useCallback, useMemo } from 'react';

import { logger } from '@zep/core-logger';

import { useBuildAiQuizByWebsite } from '@/api/ai/useBuildAiQuiz';
import { useWebsiteErrorAlert } from '@/api/ai/website';
import { type WebsiteData } from '@/api/ai/website/types';
import { quizLevelToApiQuizDifficulty } from '@/shared/features/ai-quiz-generation/constants';
import { type BuildPromptOptions } from '@/shared/features/ai-quiz-generation/types';
import { useOpenWebsiteUrlModal } from '@/views/build/modal/WebsiteUrlModal';

import {
  AI_GENERATION_EVENT_KEY,
  getPromptData,
  getStandardCodeData,
} from '../utils/getAiEventData';

import { type AchievementStandard, type UseAiByMethodParams } from './types';

/**
 * Website 방식 AI 퀴즈 생성 커스텀 훅
 *
 * Website URL을 첨부하여 퀴즈를 생성하는 Website 생성 방식을 위한
 * UseAiByMethodReturn 인터페이스 구현체
 *
 * 주요 기능:
 * - overlay-kit Promise 패턴으로 WebsiteUrlModal 호출 및 데이터 수신
 * - Website URL 첨부 및 제거 기능
 * - React Query mutation으로 POST /quiz-builder/generate/website API 호출
 * - 콜백을 통해 성공/오류 처리를 상위 컴포넌트에 위임
 * - Query/File/YouTube 방식과의 일관성을 위한 공통 인터페이스 구현
 *
 * @param params - onGenerate, onError 콜백을 포함한 훅 파라미터
 * @returns UseAiByMethodReturn 인터페이스 구현
 */
export const useAiByWebsite = (params: UseAiByMethodParams) => {
  const {
    onGenerateSuccess,
    onGenerateStart,
    onGenerateError,
    onAttach,
    onDetach,
    tryCount,
    generationId,
  } = params;

  // API 클라이언트의 mutation 사용
  const { mutateAsync } = useBuildAiQuizByWebsite();

  // WebsiteUrlModal을 여는 훅
  const { openWebsiteUrlModal } = useOpenWebsiteUrlModal();

  // /generate/website 실패 시 user-facing 알림 — preview/generate가 공통의
  // AlertGroup 분류를 공유한다. preview는 useWebsitePreview에서 직접 호출.
  const alert = useWebsiteErrorAlert('generate');

  /**
   * /generate/website 실패 시의 로깅 책임을 한곳에 모은다.
   * - Sentry 캡처 (Error 인스턴스가 아닌 비정상 throw 케이스)
   * - 표준 BI 이벤트 (`biz_ai_generation_failed`)
   * - 레거시 KO 이벤트 (대시보드 호환). 신규 채널은 hook이 발화하는
   *   `ai_website_generate_error` (alert_group/http_status 포함).
   */
  const trackGenerationFailure = useCallback(
    (error: unknown, startTime: number) => {
      if (!(error instanceof Error)) {
        logger.captureError(error, {
          fingerprint: 'website_generate_error',
        });
      }
      logger.track('biz_ai_generation_failed', {
        generation_id: generationId,
        error_type: error instanceof Error ? error.name : 'unknown',
        attempt_no: tryCount,
        latency_ms: Date.now() - startTime,
        source_type: 'website',
      });
      logger.track(
        tryCount > 0
          ? 'ai_문제생성_웹사이트_다시생성_에러'
          : 'ai_문제생성_웹사이트_시작_에러',
      );
    },
    [generationId, tryCount],
  );

  /**
   * Website URL 첨부 처리
   * overlay-kit Promise 패턴으로 WebsiteUrlModal을 열고 website 데이터를 수신
   */
  const handleAttach = useCallback(
    async (initialWebsiteData?: WebsiteData) => {
      // WebsiteUrlModal 열기
      const result = await openWebsiteUrlModal(initialWebsiteData);
      if (result) {
        onAttach(result);
      }
    },
    [openWebsiteUrlModal, onAttach],
  );

  /**
   * Website URL 첨부 해제 처리
   * 첨부된 website를 제거하고 상태를 초기화
   */
  const handleDetach = useCallback(() => {
    onDetach();
  }, [onDetach]);

  /**
   * Website URL로부터 퀴즈 생성 처리
   * website 존재 여부를 확인하고, API mutation을 호출하며, 적절한 콜백을 실행
   */
  const handleGenerate = useCallback(
    async (
      attachment: WebsiteData,
      options: BuildPromptOptions,
      standards: AchievementStandard[],
      userPrompt?: string,
      // omit (undefined) 시 서버 기본 모델 결정. mutation factory 가 snake_case 변환.
      generationMode?: 'fast' | 'thorough',
      // Stage 1 UI cohort. wire 시 ui_cohort 로 변환. dropdown 일 때만
      // 서버가 Stage 2 backend_cohort (real/placebo) 를 평가한다.
      uiCohort?: 'as_is' | 'dropdown',
    ) => {
      // Website URL 첨부 여부 확인
      onGenerateStart(tryCount > 0);

      const startTime = Date.now();

      try {
        const response = await mutateAsync({
          websiteUrl: attachment.url,
          scrapeId: attachment.scrapeId,
          type: options.quizType,
          difficulty: quizLevelToApiQuizDifficulty(options.quizLevel),
          quizCount: options.quizCount,
          tryCount,
          locale: options.language,
          userPrompt,
          selectedStandardCodes: standards.map(standard => standard.code),
          generationId,
          generationMode,
          uiCohort,
        });
        // 생성된 퀴즈 아이템과 함께 상위 컴포넌트의 onGenerate 콜백 호출
        onGenerateSuccess(response.questions);
        // 백엔드가 일시적으로 빈 배열을 반환해도 onGenerateSuccess는 정상 처리
        // 되어야 하므로, 샘플 로깅 필드는 첫 문항이 있을 때만 채운다.
        const sampleQuestion = response.questions[0];
        const completedEventPayload = {
          ...options,
          ...getPromptData(
            {
              type: 'website' as const,
              data: attachment,
            },
            userPrompt || '',
          ),
          ...getStandardCodeData(standards),
          is_retry: tryCount > 0,
          requested_question_count: options.quizCount,
          sample_question: sampleQuestion?.question ?? null,
          sample_answer: sampleQuestion?.answer ?? null,
          sample_options: sampleQuestion?.options ?? null,
          sample_type: sampleQuestion?.type ?? null,
          latency_ms: Date.now() - startTime,
          generation_id: response.generationId || generationId,
        };
        logger.track(AI_GENERATION_EVENT_KEY, completedEventPayload);
        logger.track('biz_ai_generation_completed', completedEventPayload);
      } catch (error: unknown) {
        onGenerateError();
        trackGenerationFailure(error, startTime);
        // backend의 stable error_code를 AlertGroup으로 매핑해 alert.
        alert.fromApiError(error);
      }
    },
    [
      onGenerateSuccess,
      onGenerateError,
      tryCount,
      onGenerateStart,
      mutateAsync,
      generationId,
      alert,
      trackGenerationFailure,
    ],
  );

  // 불필요한 리렌더링 방지를 위해 반환 객체 메모이제이션
  return useMemo(
    () => ({
      handleAttach,
      handleDetach,
      handleGenerate,
    }),
    [handleAttach, handleDetach, handleGenerate],
  );
};
